/**
 * What a resource needs, and which of those needs are actually a problem.
 *
 * Every dependency checker in this space starts the same way: read the
 * `dependencies` block, look for each name in the folder that was scanned, and
 * report the ones that are not there. That implementation is wrong on the first
 * real resource it meets, and wrong in the direction that destroys trust:
 *
 *   dependency 'chat'          -- ships with the server, not with anybody's pack
 *   dependency 'ox_lib'        -- installed once per server, shared by everything
 *   dependency '/server:6683'  -- not a resource at all, it is a build floor
 *   provide 'es_extended'      -- qbx_core answers to this name on purpose
 *   client_script '@ox_lib/init.lua'  -- a real path, in somebody else's folder
 *
 * A naive checker calls all five of those missing. The author looks at a red
 * report listing the five most normal lines in modern FiveM, concludes the tool
 * does not know what it is looking at, and never opens it again. The cost of a
 * false positive here is not a wasted minute; it is the whole report.
 *
 * So this module separates "I could not find it" from "it is not yours to find".
 * A name resolves to `local`, `provided`, `system`, `external`, `inferred` or
 * `missing`, and only the last one is a fault. `system` and `external` produce
 * no finding at all — that is the entire point of the module.
 *
 * The second half is the graph. Load order in FiveM is whatever `server.cfg`
 * says it is, so a resource whose dependency is `ensure`d after it starts up,
 * calls an export that does not exist yet, and fails in a way that goes away on
 * the next restart. The order is therefore worth computing, and the two failures
 * to avoid while computing it are: silently dropping a resource because it sits
 * in a cycle, and reporting "cycle detected" with no members, which tells the
 * reader nothing they can act on. Cycles come back as the smallest real loop —
 * `a -> b -> a` — and the load order collapses each loop rather than giving up.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * SCOPE — what this deliberately does not do
 *
 * Inference from script text is a hint, not a proof. `exports['ox_target']` is
 * strong evidence; `TriggerEvent('esx:getSharedObject')` is not, because that
 * name is a convention half the ecosystem copies and the resource that answers
 * it is called `es_extended`. Both come back, each tagged with the `via` string
 * that produced it, so the caller can weight them; the event forms are the low-
 * confidence ones and this module never turns one into an error.
 *
 * It also does not know what is installed on the server. `external` means "this
 * is a known framework", not "this is present". An unknown name stays `missing`,
 * because "I have never heard of it" and "it is fine" are different answers.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import {
  frameworkResourceName,
  isFrameworkResource,
} from "./data/frameworkResources.v1";
import {
  isServerProvidedResource,
  serverProvidedResourceName,
} from "./data/serverProvidedResources.v1";
import {
  declaredPaths,
  entriesOfKind,
  parseResourceInclude,
  stripLuaComments,
} from "./manifest";
import { closestMatch, normalizeName, positionAt } from "./normalize";
import type {
  DependencyEdge,
  DependencyEvidence,
  DependencyGraph,
  DependencyNode,
  DependencyStatus,
  GtaFinding,
  GtaSourceSpan,
  ManifestConstraint,
  ParsedManifest,
} from "./types";

// ─── Limits ─────────────────────────────────────────────────────────────────

/**
 * Every loop below walks data a stranger wrote, so every loop below has a
 * ceiling. The numbers are far above any real project and far below anything
 * that would stall the surface named in each comment.
 */

/** Cap the scripts scanned per resource, so a `node_modules` dump cannot hang
 *  the PackBench pre-export scan. */
const MAX_SCRIPTS_PER_RESOURCE = 500;

/** Cap the bytes read from one script before it is scanned. A bundled 20 MB
 *  `.lua` is not source a human reads, and the desktop scanner must not stall
 *  on one. Truncation only drops the tail, so every offset stays honest. */
const MAX_SCRIPT_BYTES = 2 * 1024 * 1024;

/** Cap the distinct hints taken from one script, so a generated file cannot
 *  fill the Code Visualizer dependency panel with thousands of rows. */
const MAX_INFERRED_PER_SCRIPT = 500;

/** Cap the resources folded into one graph — the desktop app can be pointed at
 *  a whole `resources/` tree, and the graph view has to stay drawable. */
const MAX_GRAPH_RESOURCES = 2000;

/** Cap the nodes in one graph, for the same view. */
const MAX_GRAPH_NODES = 8000;

/** Cap the names read out of a `DependencyContext`, so a caller handing us a
 *  generated list cannot spin forever inside a classification call. */
const MAX_CONTEXT_NAMES = 20000;

/** Above this, no load order is offered at all: Kahn is linear, but the ensure
 *  block it feeds is meant to be pasted into `server.cfg` by a person. */
const MAX_ORDER_NODES = 8000;

/** Cap the starting points tried when looking for the smallest loop inside one
 *  tangled component. Beyond this the loop shown is simply the best of the
 *  first few hundred, which is still a loop the reader can act on. */
const MAX_CYCLE_SEARCH_STARTS = 200;

/** Cap the `ensure` lines emitted, so the copy block stays a copy block. */
const MAX_ENSURE_LINES = 2000;

/** Cap the findings returned, so one broken folder cannot flood a report panel. */
const MAX_DEPENDENCY_FINDINGS = 500;

// ─── Evidence vocabulary ────────────────────────────────────────────────────

/**
 * The `via` strings, written once so a UI can group on them and so the low-
 * confidence set below cannot drift away from the strings it names.
 */
const VIA_DEPENDENCY = "dependency";
const VIA_PROVIDE = "provide";
const VIA_INCLUDE = "@resource include";
const VIA_EXPORTS_INDEX = "exports[…]";
const VIA_EXPORTS_FIELD = "exports.…";
const VIA_RESOURCE_STATE = "GetResourceState";
const VIA_TRIGGER_EVENT = "TriggerEvent";
const VIA_TRIGGER_SERVER_EVENT = "TriggerServerEvent";
const VIA_TRIGGER_CLIENT_EVENT = "TriggerClientEvent";

/**
 * Evidence that came from a manifest. A name backed by one of these was
 * declared by the author, so failing to resolve it is `missing`; a name backed
 * only by script text was never declared, so it is `inferred` instead.
 */
const DECLARED_VIA: ReadonlySet<string> = new Set([
  VIA_DEPENDENCY,
  VIA_PROVIDE,
  VIA_INCLUDE,
]);

/**
 * Event names prove a convention, not a dependency.
 *
 * `esx:getSharedObject` is fired by resources that have nothing to do with
 * `es_extended`, and the resource that answers it is not called `esx`. Treating
 * the prefix as a resource name would invent a dependency on a resource that
 * does not exist, so these hints exist to be weighted down, never to fail a
 * check.
 */
const LOW_CONFIDENCE_VIA: ReadonlySet<string> = new Set([
  VIA_TRIGGER_EVENT,
  VIA_TRIGGER_SERVER_EVENT,
  VIA_TRIGGER_CLIENT_EVENT,
]);

// ─── Defensive reading ──────────────────────────────────────────────────────

/**
 * The guards that keep a bad input from taking out the whole report.
 *
 * Everything here arrives from an adapter that walked a folder, unpacked a zip
 * or decoded a file, and one of those handing back a half-built object is a
 * normal Tuesday. A checker that throws in that situation does not report one
 * broken resource, it reports nothing at all: the panel is empty and the user
 * cannot tell whether their pack is fine or the tool fell over. So every entry
 * point reads what it was given rather than trusting it, and an unreadable input
 * produces an empty answer.
 */

function isText(value: unknown): value is string {
  return typeof value === "string";
}

function isIterable(value: unknown): value is Iterable<string> {
  return (
    value !== null &&
    (typeof value === "object" || typeof value === "function") &&
    typeof (value as { [Symbol.iterator]?: unknown })[Symbol.iterator] === "function"
  );
}

function asArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : [];
}

/** A graph reduced to the parts that are actually there. */
function readableGraph(graph: DependencyGraph): {
  nodes: DependencyNode[];
  cycles: string[][];
  order: string[] | null;
} {
  const source = graph !== null && typeof graph === "object" ? graph : null;
  const nodes = asArray<DependencyNode>(source?.nodes).filter(
    (node) => node !== null && typeof node === "object" && isText(node.id),
  );
  const cycles = asArray<string[]>(source?.cycles).filter(
    (cycle) => Array.isArray(cycle) && cycle.length > 0,
  );
  const order = Array.isArray(source?.order) ? source.order.filter(isText) : null;
  return { nodes, cycles, order };
}

/** A manifest we can actually read entries out of, or nothing. */
function readable(manifest: ParsedManifest | null): ParsedManifest | null {
  if (!manifest || typeof manifest !== "object") return null;
  return Array.isArray(manifest.entries) ? manifest : null;
}

// ─── Classification ─────────────────────────────────────────────────────────

export type DependencyContext = {
  localResources: Iterable<string>;
  provided?: Map<string, string>;
};

type ContextIndex = { local: Set<string>; provided: Set<string> };

/**
 * Normalized lookups for a context, remembered against the context object.
 *
 * Two reasons, and the second one is a correctness bug rather than a speed
 * problem. Speed: a graph classifies every node against the same context, and
 * re-walking the resource list each time is quadratic on a `resources/` folder
 * with two thousand entries. Correctness: `localResources` is an `Iterable`, so
 * a caller may legitimately hand us a generator — which yields nothing the
 * second time it is read, and would make every dependency after the first look
 * missing. Materializing once fixes both.
 *
 * The context is treated as immutable for the life of the object. A caller that
 * mutates the array it passed in should pass a new context.
 */
const CONTEXT_INDEX = new WeakMap<DependencyContext, ContextIndex>();

function contextIndex(context: DependencyContext): ContextIndex {
  const cached = CONTEXT_INDEX.get(context);
  if (cached) return cached;

  const local = new Set<string>();
  if (isIterable(context.localResources)) {
    let seen = 0;
    for (const name of context.localResources) {
      if (seen >= MAX_CONTEXT_NAMES) break;
      seen += 1;
      if (isText(name)) local.add(normalizeName(name));
    }
  }

  const provided = new Set<string>();
  if (context.provided instanceof Map) {
    let aliases = 0;
    for (const alias of context.provided.keys()) {
      if (aliases >= MAX_CONTEXT_NAMES) break;
      aliases += 1;
      if (isText(alias)) provided.add(normalizeName(alias));
    }
  }

  const index: ContextIndex = { local, provided };
  CONTEXT_INDEX.set(context, index);
  return index;
}

/**
 * What a name in a `dependencies` block is, in the order the answers matter.
 *
 * The order is the rule. A resource that is in this project is `local` even if a
 * framework of the same name exists, because the local one is what will load. An
 * alias somebody `provide`s beats the framework list for the same reason:
 * `qbx_core` provides `es_extended`, and on that server `es_extended` is not an
 * external download, it is the resource next door.
 *
 * `inferred` is not decidable here and never returned. This function is given a
 * name, not the script that mentioned it, so it cannot know whether the name was
 * declared or merely used; `buildDependencyGraph` holds that context and
 * promotes an otherwise-`missing` name to `inferred` when the only evidence for
 * it came out of script text.
 */
export function classifyDependency(name: string, context: DependencyContext): DependencyStatus {
  if (!isText(name) || context === null || typeof context !== "object") return "missing";
  const normalized = normalizeName(name);
  if (normalized === "") return "missing";

  // Defensive. `parseManifest` routes `/server:6683` into `constraints` long
  // before it could reach here, and the memorable false positive of the previous
  // generation of these tools was "dependency '/server:6683' is missing".
  if (normalized.startsWith("/")) return "constraint";

  const index = contextIndex(context);
  if (index.local.has(normalized)) return "local";
  if (index.provided.has(normalized)) return "provided";
  if (isServerProvidedResource(normalized)) return "system";
  if (isFrameworkResource(normalized)) return "external";
  return "missing";
}

// ─── Reading script text ────────────────────────────────────────────────────

/**
 * The comment rule lives in `manifest.ts`, and it lives there once.
 *
 * Matching on raw source invents dependencies out of history: the line somebody
 * commented out last month is still text, and a regex reports the resource it
 * names as a live dependency. `stripLuaComments` blanks comments in place, so
 * every offset in the result still points at the same byte of the original and
 * a string like `"SELECT 1 -- limit"` survives intact. Re-implementing that here
 * would give the layer two ideas about what a comment is, which is exactly the
 * kind of disagreement this shared module exists to end.
 */

type InferredDependency = { resource: string; via: string; span: GtaSourceSpan };

/**
 * Resource names a script mentions, with where it mentioned them.
 *
 * Four of the five patterns name a resource outright and are worth acting on.
 * The event forms name an event, and the text before the colon is a convention;
 * they come back anyway, tagged with the call that produced them, because a
 * reader looking at a graph wants to see why an edge is there even when the edge
 * is a guess.
 *
 * Repeats are collapsed: a file that calls `exports['ox_target']` two hundred
 * times has one dependency on `ox_target`, and the span kept is the first one,
 * which is where a reader should look.
 */
export function inferDependencies(
  source: string,
  options?: { file?: string },
): InferredDependency[] {
  if (!isText(source)) return [];
  const text = stripLuaComments(source.slice(0, MAX_SCRIPT_BYTES));
  const file = isText(options?.file) ? options.file : undefined;
  const found: InferredDependency[] = [];
  const seen = new Set<string>();

  const record = (resource: string, via: string, start: number, end: number): void => {
    const name = resource.trim();
    if (name === "" || found.length >= MAX_INFERRED_PER_SCRIPT) return;
    const key = `${normalizeName(name)}\u0000${via}`;
    if (seen.has(key)) return;
    seen.add(key);
    const { line, column } = positionAt(text, start);
    const span: GtaSourceSpan = { line, column, byteStart: start, byteEnd: end };
    if (file !== undefined) span.file = file;
    found.push({ resource: name, via, span });
  };

  // `client_script '@ox_lib/init.lua'` inside a script, and every documented
  // `@resource/path` reference. The path is real; it is in somebody else's
  // folder, which is exactly why reading it as a local file reports the
  // documented way to use every major library as a missing file.
  const include = /@([A-Za-z0-9_.-]+)\/[A-Za-z0-9_./-]+/g;
  for (const match of text.matchAll(include)) {
    record(match[1], VIA_INCLUDE, match.index ?? 0, (match.index ?? 0) + match[0].length);
  }

  // `exports['ox_target']` and `exports["ox_target"]`. The bracket form is the
  // only one that can carry a hyphen, which is why `qb-core` is always written
  // this way.
  const exportsIndex = /\bexports\s*\[\s*(['"])([^'"\n]+)\1\s*\]/g;
  for (const match of text.matchAll(exportsIndex)) {
    record(match[2], VIA_EXPORTS_INDEX, match.index ?? 0, (match.index ?? 0) + match[0].length);
  }

  // `exports.ox_lib`. A Lua identifier, so no hyphens reach this branch.
  const exportsField = /\bexports\s*\.\s*([A-Za-z_][A-Za-z0-9_]*)/g;
  for (const match of text.matchAll(exportsField)) {
    record(match[1], VIA_EXPORTS_FIELD, match.index ?? 0, (match.index ?? 0) + match[0].length);
  }

  // `GetResourceState('ox_lib') ~= 'started'` — the guard people write instead
  // of a dependency line, and the strongest hint in the set.
  const resourceState = /\bGetResourceState\s*\(\s*(['"])([^'"\n]+)\1/g;
  for (const match of text.matchAll(resourceState)) {
    record(match[2], VIA_RESOURCE_STATE, match.index ?? 0, (match.index ?? 0) + match[0].length);
  }

  // Event names. The prefix before the first colon is a convention, so this is
  // recorded as a hint and weighted down everywhere it is consumed.
  const events =
    /\b(TriggerEvent|TriggerServerEvent|TriggerClientEvent)\s*\(\s*(['"])([^'"\n]+)\2/g;
  for (const match of text.matchAll(events)) {
    const eventName = match[3];
    const colon = eventName.indexOf(":");
    if (colon <= 0) continue;
    const prefix = eventName.slice(0, colon);
    if (!/^[A-Za-z0-9_.-]+$/.test(prefix)) continue;
    record(prefix, match[1], match.index ?? 0, (match.index ?? 0) + match[0].length);
  }

  return found;
}

// ─── Building the graph ─────────────────────────────────────────────────────

type DependencyGraphInput = {
  name: string;
  manifest: ParsedManifest | null;
  scripts?: { file: string; source: string }[];
};

/**
 * Fold a set of resources into one graph of who needs whom.
 *
 * Edges point from the dependent to the dependency — `from` needs `to` — which
 * is the direction a load order reads backwards from, and the direction a graph
 * UI draws arrows in.
 *
 * A `provide` gets its own node and its own edge, alias to provider, rather than
 * being silently rewritten to the provider's name. A server running `qbx_core`
 * genuinely has something called `es_extended` on it, and a reader who wrote
 * `dependency 'es_extended'` should see that resolve to the resource that
 * answers for it, not see their own line quietly renamed.
 */
export function buildDependencyGraph(resources: DependencyGraphInput[]): DependencyGraph {
  const list = asArray<DependencyGraphInput>(resources)
    .filter(
      (resource) => resource !== null && typeof resource === "object" && isText(resource.name),
    )
    .slice(0, MAX_GRAPH_RESOURCES);

  const localNames: string[] = [];
  const provided = new Map<string, string>();
  for (const resource of list) {
    localNames.push(resource.name);
    const manifest = readable(resource.manifest);
    if (!manifest) continue;
    for (const entry of entriesOfKind(manifest, "provide")) {
      const alias = normalizeName(entry.value);
      if (alias === "" || alias === normalizeName(resource.name)) continue;
      if (!provided.has(alias)) provided.set(alias, normalizeName(resource.name));
    }
  }
  const context: DependencyContext = { localResources: localNames, provided };

  const nodes = new Map<string, DependencyNode>();
  const declared = new Set<string>();
  const edges: DependencyEdge[] = [];
  const edgeKeys = new Set<string>();
  const constraints: ManifestConstraint[] = [];

  const touch = (rawName: string, label: string): DependencyNode | null => {
    if (!isText(rawName)) return null;
    const id = normalizeName(rawName);
    if (id === "") return null;
    const existing = nodes.get(id);
    if (existing) return existing;
    if (nodes.size >= MAX_GRAPH_NODES) return null;
    const node: DependencyNode = { id, label, status: "missing", evidence: [] };
    nodes.set(id, node);
    return node;
  };

  const connect = (from: string, to: string, kind: DependencyEdge["kind"]): void => {
    const fromId = normalizeName(from);
    const toId = normalizeName(to);
    // A resource that names itself is not a cycle, it is a typo or a self
    // include, and drawing the loop would bury the real ones.
    if (fromId === "" || toId === "" || fromId === toId) return;
    const key = `${fromId}\u0000${toId}\u0000${kind}`;
    if (edgeKeys.has(key)) return;
    edgeKeys.add(key);
    edges.push({ from: fromId, to: toId, kind });
  };

  const note = (
    target: DependencyNode | null,
    via: string,
    from: string,
    span?: GtaSourceSpan,
  ): void => {
    if (!target) return;
    const evidence: DependencyEvidence = { via, from };
    if (span) evidence.span = span;
    target.evidence.push(evidence);
    if (DECLARED_VIA.has(via)) declared.add(target.id);
  };

  // Pass one: every resource in the set is a node, labelled with the project's
  // own spelling, before anything else gets a chance to name it differently.
  for (const resource of list) touch(resource.name, resource.name);

  for (const resource of list) {
    const owner = resource.name;
    const manifest = readable(resource.manifest);

    if (manifest) {
      for (const constraint of asArray<ManifestConstraint>(manifest.constraints)) {
        constraints.push(constraint);
      }

      for (const entry of entriesOfKind(manifest, "dependency")) {
        const status = classifyDependency(entry.value, context);
        const target = touch(entry.value, canonicalLabel(entry.value));
        note(target, VIA_DEPENDENCY, owner, entry.span);
        // A constraint is a server build floor, not something to load first.
        if (status !== "constraint" && target) connect(owner, target.id, "declared");
      }

      for (const entry of entriesOfKind(manifest, "provide")) {
        const target = touch(entry.value, canonicalLabel(entry.value));
        note(target, VIA_PROVIDE, owner, entry.span);
        if (target) connect(target.id, owner, "provided");
      }

      for (const entry of declaredPaths(manifest)) {
        const include = parseResourceInclude(entry.value);
        if (!include) continue;
        const target = touch(include.resource, canonicalLabel(include.resource));
        note(target, VIA_INCLUDE, owner, entry.span);
        if (target) connect(owner, target.id, "declared");
      }
    }

    const scripts = asArray<{ file: string; source: string }>(resource.scripts)
      .filter((script) => script !== null && typeof script === "object")
      .slice(0, MAX_SCRIPTS_PER_RESOURCE);
    for (const script of scripts) {
      for (const hint of inferDependencies(script.source, { file: script.file })) {
        if (normalizeName(hint.resource) === normalizeName(owner)) continue;
        const target = touch(hint.resource, canonicalLabel(hint.resource));
        note(target, hint.via, owner, hint.span);
        if (target) connect(owner, target.id, "inferred");
      }
    }
  }

  // Pass two: classify. Everything that could be known is known by now, which
  // is what lets an undeclared-but-used name come out as `inferred` rather than
  // as the error a declared-and-absent name deserves.
  for (const node of nodes.values()) {
    const status = classifyDependency(node.id, context);
    node.status = status === "missing" && !declared.has(node.id) ? "inferred" : status;
  }

  const ids = [...nodes.keys()];
  const cycles = findCycles(ids, edges);
  return {
    nodes: [...nodes.values()],
    edges,
    cycles,
    order: loadOrder(ids, edges),
    constraints,
  };
}

/**
 * The spelling to print for a name nobody in this project owns.
 *
 * `gameinit` is written `gameInit` in the FiveM docs and `polyzone` is written
 * `PolyZone` by its author, and a report that echoes the user's casing back at
 * them is a report they cannot search the internet with.
 */
function canonicalLabel(name: string): string {
  if (!isText(name)) return "";
  return serverProvidedResourceName(name) ?? frameworkResourceName(name) ?? name.trim();
}

// ─── Cycles ─────────────────────────────────────────────────────────────────

/** Dependency adjacency, restricted to known nodes and deterministically ordered. */
function adjacencyOf(
  nodes: readonly string[],
  edges: readonly DependencyEdge[],
): Map<string, string[]> {
  const known = new Set(nodes);
  const adjacency = new Map<string, string[]>();
  for (const id of nodes) adjacency.set(id, []);
  for (const edge of edges) {
    if (!edge || !isText(edge.from) || !isText(edge.to)) continue;
    if (!known.has(edge.from) || !known.has(edge.to) || edge.from === edge.to) continue;
    const list = adjacency.get(edge.from);
    if (list && !list.includes(edge.to)) list.push(edge.to);
  }
  for (const list of adjacency.values()) list.sort();
  return adjacency;
}

/**
 * Tarjan's strongly connected components, iteratively.
 *
 * Iteratively because the recursive form is one call frame per node, and this
 * runs in a browser tab over a `resources/` folder that can hold thousands of
 * them. A stack overflow here would take out the whole report.
 */
function stronglyConnected(
  nodes: readonly string[],
  adjacency: Map<string, string[]>,
): string[][] {
  const index = new Map<string, number>();
  const low = new Map<string, number>();
  const onStack = new Set<string>();
  const stack: string[] = [];
  const components: string[][] = [];
  let counter = 0;

  for (const root of nodes) {
    if (index.has(root)) continue;
    index.set(root, counter);
    low.set(root, counter);
    counter += 1;
    stack.push(root);
    onStack.add(root);
    const work: { node: string; edge: number }[] = [{ node: root, edge: 0 }];

    while (work.length > 0) {
      const frame = work[work.length - 1];
      const neighbours = adjacency.get(frame.node) ?? [];

      if (frame.edge < neighbours.length) {
        const next = neighbours[frame.edge];
        frame.edge += 1;
        if (!index.has(next)) {
          index.set(next, counter);
          low.set(next, counter);
          counter += 1;
          stack.push(next);
          onStack.add(next);
          work.push({ node: next, edge: 0 });
        } else if (onStack.has(next)) {
          low.set(frame.node, Math.min(low.get(frame.node) ?? 0, index.get(next) ?? 0));
        }
        continue;
      }

      work.pop();
      if (low.get(frame.node) === index.get(frame.node)) {
        const component: string[] = [];
        while (stack.length > 0) {
          const member = stack.pop() as string;
          onStack.delete(member);
          component.push(member);
          if (member === frame.node) break;
        }
        components.push(component);
      }
      const parent = work[work.length - 1];
      if (parent) {
        low.set(parent.node, Math.min(low.get(parent.node) ?? 0, low.get(frame.node) ?? 0));
      }
    }
  }

  return components;
}

/**
 * The shortest loop inside one tangled component.
 *
 * A component with nine members is not a nine-step loop the reader has to
 * follow; it is nine resources with at least one short loop somewhere inside,
 * and the short one is the one worth printing. Breadth-first from each member
 * back to itself finds it, and the result is rotated to start at its smallest
 * name so the same tangle prints the same way every time.
 */
function smallestLoop(component: readonly string[], adjacency: Map<string, string[]>): string[] {
  if (component.length === 1) return [component[0]];
  const members = new Set(component);
  const starts = [...component].sort().slice(0, MAX_CYCLE_SEARCH_STARTS);
  let best: string[] | null = null;

  for (const start of starts) {
    const previous = new Map<string, string>();
    const queue: string[] = [start];
    const seen = new Set<string>([start]);
    let closes: string | null = null;
    let head = 0;

    while (head < queue.length && closes === null) {
      const node = queue[head];
      head += 1;
      for (const next of adjacency.get(node) ?? []) {
        if (!members.has(next)) continue;
        if (next === start) {
          closes = node;
          break;
        }
        if (seen.has(next)) continue;
        seen.add(next);
        previous.set(next, node);
        queue.push(next);
      }
    }

    if (closes === null) continue;
    const path: string[] = [closes];
    let cursor = closes;
    while (cursor !== start && path.length <= component.length) {
      const back = previous.get(cursor);
      if (back === undefined) break;
      path.push(back);
      cursor = back;
    }
    path.reverse();
    if (!best || path.length < best.length) best = path;
  }

  return rotateToSmallest(best ?? [...component].sort());
}

/** Same loop, always printed from the same member. */
function rotateToSmallest(loop: readonly string[]): string[] {
  let at = 0;
  for (let i = 1; i < loop.length; i += 1) {
    if (loop[i] < loop[at]) at = i;
  }
  return [...loop.slice(at), ...loop.slice(0, at)];
}

/**
 * Every cycle in the graph, smallest first.
 *
 * Components of one member are only reported when the member depends on itself,
 * which is a real thing people write and a real reason a resource never starts.
 */
export function findCycles(
  nodes: readonly string[],
  edges: readonly DependencyEdge[],
): string[][] {
  const ids = asArray<string>(nodes).filter(isText);
  const links = asArray<DependencyEdge>(edges);
  const adjacency = adjacencyOf(ids, links);
  const known = new Set(ids);
  const selfLoops = new Set<string>();
  for (const edge of links) {
    if (!edge || !isText(edge.from)) continue;
    if (edge.from === edge.to && known.has(edge.from)) selfLoops.add(edge.from);
  }

  const cycles: string[][] = [];
  for (const component of stronglyConnected(ids, adjacency)) {
    if (component.length === 1 && !selfLoops.has(component[0])) continue;
    cycles.push(smallestLoop(component, adjacency));
  }

  cycles.sort((a, b) => (a.length - b.length) || (a.join(" ") < b.join(" ") ? -1 : 1));
  return cycles;
}

// ─── Load order ─────────────────────────────────────────────────────────────

/**
 * A `server.cfg` order: everything a resource needs, before the resource.
 *
 * Cyclic groups are collapsed rather than dropped. Two resources that depend on
 * each other have no correct order, but they still have to be started, and a
 * sort that returned nothing at all for the whole folder because two of its
 * hundred resources are tangled would be useless. Each loop becomes one node,
 * represented by its first member, and the caller can expand it.
 *
 * `null` means no order at all is on offer: the graph is larger than anything a
 * person would paste into a config, or the condensation somehow still contains a
 * cycle, which would mean this function is wrong and a guess would hide it.
 */
export function loadOrder(
  nodes: readonly string[],
  edges: readonly DependencyEdge[],
): string[] | null {
  const ids = asArray<string>(nodes).filter(isText);
  const links = asArray<DependencyEdge>(edges).filter(
    (edge) => edge !== null && typeof edge === "object" && isText(edge.from) && isText(edge.to),
  );
  if (ids.length > MAX_ORDER_NODES) return null;

  const rank = new Map<string, number>();
  ids.forEach((id, at) => {
    if (!rank.has(id)) rank.set(id, at);
  });

  const representative = new Map<string, string>();
  for (const cycle of findCycles(ids, links)) {
    for (const member of cycle) representative.set(member, cycle[0]);
  }
  const repOf = (id: string): string => representative.get(id) ?? id;

  const groups: string[] = [];
  const groupSeen = new Set<string>();
  for (const id of ids) {
    const rep = repOf(id);
    if (groupSeen.has(rep)) continue;
    groupSeen.add(rep);
    groups.push(rep);
  }

  const pending = new Map<string, number>(groups.map((id) => [id, 0]));
  const dependents = new Map<string, string[]>(groups.map((id) => [id, []]));
  const edgeSeen = new Set<string>();
  for (const edge of links) {
    if (!rank.has(edge.from) || !rank.has(edge.to)) continue;
    const from = repOf(edge.from);
    const to = repOf(edge.to);
    if (from === to) continue;
    const key = `${from}\u0000${to}`;
    if (edgeSeen.has(key)) continue;
    edgeSeen.add(key);
    pending.set(from, (pending.get(from) ?? 0) + 1);
    (dependents.get(to) as string[]).push(from);
  }

  const byRank = (a: string, b: string): number =>
    (rank.get(a) ?? 0) - (rank.get(b) ?? 0) || (a < b ? -1 : 1);

  const order: string[] = [];
  let frontier = groups.filter((id) => (pending.get(id) ?? 0) === 0).sort(byRank);
  // Bounded by the group count: each group enters `frontier` exactly once.
  while (frontier.length > 0) {
    order.push(...frontier);
    const next: string[] = [];
    for (const id of frontier) {
      for (const dependent of dependents.get(id) ?? []) {
        const left = (pending.get(dependent) ?? 0) - 1;
        pending.set(dependent, left);
        if (left === 0) next.push(dependent);
      }
    }
    frontier = next.sort(byRank);
  }

  return order.length === groups.length ? order : null;
}

// ─── The ensure block ───────────────────────────────────────────────────────

/**
 * The lines to paste into `server.cfg`, and nothing else.
 *
 * Only resources this project actually ships get a line. `ensure chat` is
 * harmless but wrong — the server already started it — and `ensure ox_lib` in a
 * pack that does not contain `ox_lib` is a config that fails at boot on a server
 * where the library lives somewhere else in the load order. A missing resource
 * gets no line either: there is nothing to start.
 */
export function ensureBlock(graph: DependencyGraph): string {
  const known = readableGraph(graph);
  const byId = new Map(known.nodes.map((node) => [node.id, node] as const));
  const localLabel = (id: string): string | null => {
    const node = byId.get(id);
    return node && node.status === "local" ? node.label : null;
  };

  if (known.order === null) {
    const lines = [
      "# Load order could not be worked out, so this list is alphabetical: check it by hand.",
    ];
    const labels = known.nodes
      .filter((node) => node.status === "local")
      .map((node) => node.label)
      .sort()
      .slice(0, MAX_ENSURE_LINES);
    for (const label of labels) lines.push(`ensure ${label}`);
    return lines.join("\n");
  }

  const lines = ["# Load order: each resource after the ones it depends on."];
  const cycleByRepresentative = new Map(known.cycles.map((cycle) => [cycle[0], cycle] as const));
  let written = 0;

  for (const id of known.order) {
    if (written >= MAX_ENSURE_LINES) break;
    const cycle = cycleByRepresentative.get(id);
    const labels = (cycle ?? [id])
      .map(localLabel)
      .filter((label): label is string => label !== null);
    if (labels.length === 0) continue;
    if (cycle && labels.length > 1) {
      lines.push(`# ${labels.join(" and ")} depend on each other; this order is a guess.`);
    }
    for (const label of labels) {
      lines.push(`ensure ${label}`);
      written += 1;
    }
  }

  return lines.join("\n");
}

// ─── Findings ───────────────────────────────────────────────────────────────

/** The resources that named a node, for a message that says who is affected. */
function claimants(node: DependencyNode): string[] {
  const names: string[] = [];
  for (const evidence of node.evidence) {
    if (!names.includes(evidence.from)) names.push(evidence.from);
  }
  return names;
}

function firstSpan(node: DependencyNode): GtaSourceSpan {
  for (const evidence of node.evidence) {
    if (evidence.span) return evidence.span;
  }
  return {};
}

function joinNames(names: readonly string[]): string {
  if (names.length === 0) return "this resource";
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

/**
 * What is worth telling somebody about their dependencies.
 *
 * Three things, and the silence around them matters as much as they do. A
 * `system` name and an `external` name produce nothing: they are correct, they
 * are the majority of every real dependency block, and reporting them is how a
 * report becomes noise nobody reads.
 */
export function dependencyFindings(graph: DependencyGraph): GtaFinding[] {
  const known = readableGraph(graph);
  const findings: GtaFinding[] = [];
  const localLabels = known.nodes
    .filter((node) => node.status === "local")
    .map((node) => node.label);

  for (const node of known.nodes) {
    if (findings.length >= MAX_DEPENDENCY_FINDINGS) break;

    if (node.status === "missing") {
      const needed = joinNames(claimants(node));
      const suggestion = closestMatch(node.label, localLabels);
      const detail =
        `Nothing in this project is called ${node.label}, and it is not a resource the ` +
        `server ships or a framework we know by name. The server will refuse to start ` +
        `${needed} until ${node.label} is running, so every export, command and event it ` +
        `registers is simply absent in game. Install ${node.label} on the server, or drop ` +
        `the dependency line if it is no longer used.` +
        (suggestion
          ? ` This project does ship ${suggestion.value}, which is close enough to be a typo.`
          : "");
      findings.push({
        ...firstSpan(node),
        code: "dependency.missing",
        severity: "error",
        title: `Dependency ${node.label} is not in this project`,
        detail,
        entity: { kind: "dependency", id: node.id },
        ...(suggestion
          ? {
              fix: {
                label: `Change the dependency to ${suggestion.value}`,
                confidence: suggestion.confidence >= 0.85 ? ("medium" as const) : ("low" as const),
              },
            }
          : {}),
      });
      continue;
    }

    if (node.status === "inferred") {
      const users = joinNames(claimants(node));
      const vias = [...new Set(node.evidence.map((evidence) => evidence.via))];
      const onlyEvents = vias.every((via) => LOW_CONFIDENCE_VIA.has(via));
      const detail =
        `${users} calls into ${node.label} (seen through ${vias.join(", ")}) but no manifest ` +
        `declares it, so nothing makes the server start ${node.label} first. When it does not, ` +
        `the export comes back nil at boot and the feature is dead until somebody restarts the ` +
        `resource by hand — which is why this kind of fault looks intermittent. ` +
        (onlyEvents
          ? `The evidence here is an event name, which follows a convention rather than proving ` +
            `a dependency, so confirm the resource exists before declaring it.`
          : `Add dependency '${node.label}' to the manifest if the call is real.`);
      findings.push({
        ...firstSpan(node),
        code: "dependency.inferred",
        severity: "info",
        title: `${node.label} is used but never declared`,
        detail,
        entity: { kind: "dependency", id: node.id },
        tags: ["inferred"],
        ...(onlyEvents
          ? {}
          : {
              fix: {
                label: `Add dependency '${node.label}' to fxmanifest.lua`,
                confidence: "medium" as const,
              },
            }),
      });
    }
  }

  const byId = new Map(known.nodes.map((node) => [node.id, node] as const));
  for (const cycle of known.cycles) {
    if (findings.length >= MAX_DEPENDENCY_FINDINGS) break;
    const labels = cycle.map((id) => byId.get(id)?.label ?? id);
    const loop = [...labels, labels[0]].join(" -> ");
    const detail =
      cycle.length === 1
        ? `${labels[0]} declares itself as its own dependency, which the server cannot satisfy: ` +
          `it waits for a resource that is waiting for it, and never starts. Remove the line.`
        : `These resources each wait for the next one, so no order in server.cfg satisfies all ` +
          `of them: whichever starts first calls into a resource that has not started yet, and ` +
          `its exports come back nil. Break the loop by moving the shared code into a third ` +
          `resource both can depend on, or by dropping the dependency that is only there for ` +
          `an event that fires later anyway.`;
    findings.push({
      code: "dependency.cycle",
      severity: "warning",
      title: `Dependency cycle: ${loop}`,
      detail,
      entity: { kind: "dependency", id: cycle[0] },
    });
  }

  return findings;
}
